import _ from 'lodash'

import api from '@/api'
import constants from '@/constants'
import handleError from '@/lib/handleError.js'

import types from './types'

const IMPLEMENTED = 2

const getImplementedStatus = expand => {
  if (expand.isImplemented === true) {
    return IMPLEMENTED
  }

  return null
}

export default {
  async getRev(context, { organizationId, revId }) {
    return api.rev
      .getById(organizationId, revId)
      .then(async ({ data: rev }) => {
        context.commit(types.SET_REV, { rev })
        return rev
      })
  },

  async translateRev(
    context,
    { organizationId, revId, targetLanguage, fieldsToTranslate }
  ) {
    const { data: rev } = await api.rev.getById(organizationId, revId)

    const translatedRev = Object.assign({}, rev)

    for (const field of fieldsToTranslate) {
      if (!_.get(rev, field)) continue
      const translatedField = await api.translate.text(
        organizationId,
        _.get(rev, field),
        targetLanguage
      )

      _.set(translatedRev, field, translatedField.data.translatedText)
    }

    context.commit(types.SET_REV, { rev: translatedRev })
  },

  deleteRev(context, { organizationId, revId }) {
    return api.rev.remove(organizationId, revId).then(response => {
      return response
    })
  },

  createRev: function (context, payload) {
    return api.rev
      .create(payload.organizationId, payload.data)
      .then(({ data: rev }) => {
        context.commit(types.SET_REV, { rev })
        return { revId: rev._id }
      })
      .catch(handleError)
  },

  getAccount: async function (context, payload) {
    const account = await api.account.getById(
      payload.organizationId,
      payload.accountId
    )
    return account.data
  },

  updatePartial(context, { organizationId, revId, data }) {
    return api.rev
      .update(organizationId, revId, data)
      .then(({ data: rev }) => {
        context.commit(types.SET_REV, { rev })
        context.dispatch('refreshAccountData', {}, { root: true })
        return rev
      })
      .catch(handleError)
  },

  async updateRev(context, { organizationId, revId, data }) {
    const relate = data.relate || {}
    const experiment = data.experiment || {}
    const verify = data.verify || {}
    const expand = data.expand || {}
    const review = data.review || {}
    const location = data.location || {}
    const team = data.team || {}
    const response = await api.rev.update(organizationId, revId, {
      name: data.name,
      cycle: {
        relateProblem: relate.problem,
        relateIdea: relate.idea,
        relateResult: relate.expectedResult,
        relatePhotos: relate?.media?.map?.(media => media.id) ?? [],
        experimentDone: experiment.whatDidYouDo,
        experimentPhotos: experiment?.media?.map?.(media => media.id) ?? [],
        collaborators: data.collaborators,
        verify: verify.didExperimentWork,
        expandLearn: verify.learned,
        expandPhotos: expand?.media?.map?.(media => media.id) ?? [],
        expandSustain: expand.solution,
        reviewImplemented: getImplementedStatus(expand),
        reviewNotImplementedExplanation: expand.whyNotImplemented,
        reviewTimeReductionByMonth: review.timeReductionByMonth ?? null,
        reviewTimeInvested: review.timeInvested ?? null,
        financialInformationCaptured: {
          reviewCostReductionPerMonth: {
            value: review.costReduction,
          },
          reviewNewRevenuePerMonth: {
            value: review.revenue,
          },
          reviewTotalInvestmentMade: {
            value: review.investment,
          },
        },
        reviewComments: review.estimationComment,
      },
      categories: data.categories,
      owner: team.owner,
      coach: team.coach,
      collaborators: team.collaborators,
      site: location.site,
      attributes: location.attributes,
      attachments: data.attachments,
      tags: location.tags,
      notes: data.notes,
      priority: data.priority,
      dueDate: data.dueDate,
      status: data.status,
      canceledReason: data.canceledReason,
      onHoldReason: data.onHoldReason,
      reviewPhase: data?.reviewPhase ?? {},
      impactStartsAt: data?.impactStartsAt,
      impactEndsAt: data?.impactEndsAt,
    })
    const rev = response.data
    context.commit(types.SET_REV, { rev })

    context.dispatch('refreshAccountData', {}, { root: true })

    return { revId }
  },

  likeRev: function (context, payload) {
    return api.like
      .add(payload.organizationId, 'revs', payload.revId)
      .then(() => {
        return api.rev.getById(payload.organizationId, payload.revId)
      })
      .then(({ data: rev }) => {
        context.commit(types.SET_REV, { rev })

        context.dispatch('refreshAccountData', {}, { root: true })
        return rev
      })
      .catch(handleError)
  },

  unlikeRev: function (context, payload) {
    return api.like
      .remove(payload.organizationId, 'revs', payload.revId)
      .then(() => {
        return api.rev.getById(payload.organizationId, payload.revId)
      })
      .then(({ data: rev }) => {
        context.commit(types.SET_REV, { rev })

        return rev
      })
      .catch()
  },

  approveRev: function (context, payload) {
    return api.approvals
      .add(payload.organizationId, 'improvement-ideas', payload.revId)
      .then(() => {
        return api.rev.getById(payload.organizationId, payload.revId)
      })
      .then(({ data: rev }) => {
        context.commit(types.SET_REV, { rev })

        context.dispatch('refreshAccountData', {}, { root: true })
        return rev
      })
      .catch(handleError)
  },
  disapproveRev: function (context, payload) {
    return api.approvals
      .remove(
        payload.organizationId,
        'improvement-ideas',
        payload.revId,
        payload.approvalId
      )
      .then(() => {
        return api.rev.getById(payload.organizationId, payload.revId)
      })
      .then(({ data: rev }) => {
        context.commit(types.SET_REV, { rev })
        return rev
      })
      .catch()
  },

  updateAttachments: function (
    context,
    { organizationId, revId, attachments }
  ) {
    return api.rev
      .update(organizationId, revId, {
        attachments,
      })
      .then(({ data: attachments }) => {
        return attachments
      })
  },

  getRevChallenges: async function (context, payload) {
    const sites = _.isEmpty(payload.filters?.site)
      ? undefined
      : _.castArray(payload.filters.site)
    const status = _.isEmpty(payload.status)
      ? undefined
      : _.castArray(payload.status)

    const response = await api.explore.getContent({
      organizationId: payload.organizationId,
      filters: {
        types: [constants.exploreEntityTypes.challenge],
        status,
        sites,
      },
      sort: { by: 'updatedTimestamp', order: 'desc' },
    })

    const challenges = response.data.results.map(result => result.response)

    context.commit('SET_REV_CHALLENGES', {
      challenges,
    })

    return challenges
  },

  async getIdeaMatches(context, payload) {
    const organizationId = payload.organizationId

    const response = await api.ideamatch.getIdeaMatches(organizationId, {
      rev: payload.rev,
    })

    return response.data.results
  },

  async validationRequest(context, payload) {
    const response = await api.validationRequest.add(
      payload.organizationId,
      'improvement-ideas',
      payload.revId,
      payload.assignees
    )
    return response
  },

  async updateRequestStatus(context, payload) {
    return await api.validationRequest.update(
      payload.organizationId,
      'improvement-ideas',
      payload.revId,
      payload.requestId,
      payload.status
    )
  },
}
